import { Toast } from '@youversion/design-system-components';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Displays a toast when a status is changed. This is very useful to prevent multiple toasts being displayed when only
 * wanting to show one toast.
 *
 * @param onSuccessMessage - Message to display in toast on success.
 * @param onErrorMessage - Message to display in toast on error.
 * @param isEnabled - (Optional) Set to false to prevent the toast from being displayed.
 *
 * @example
 * ```tsx
 * // If updateResult changes cause multiple renders, only one toast will be displayed.
 * function SomeComponent(updateResult: 'success' | 'failure') {
 *   const { setToastStatus } = useErrorOrSuccessToast({
 *     onErrorMessage: t('error_updating_notifications'),
 *     onSuccessMessage: t('success_notifications'),
 *   });
 *
 *   if (updateResult === 'success') {
 *     setToastStatus('success');
 *   } else {
 *     setToastStatus('error');
 *   }
 *
 *   return <>Component UI<>;
 * }
 * ```
 */
export function useErrorOrSuccessToast({
  onSuccessMessage,
  onErrorMessage,
  isEnabled = true,
}: {
  isEnabled?: boolean;
  onErrorMessage: string;
  onSuccessMessage: string;
}) {
  const [toastStatus, setToastStatus] = useState<'success' | 'error' | undefined>();

  useEffect(() => {
    if (isEnabled) {
      if (toastStatus === 'success') {
        toast.custom(
          <Toast dismiss={() => setToastStatus(undefined)} duration={10000} position='inlineEnd' variant='success'>
            {onSuccessMessage}
          </Toast>
        );
      } else if (toastStatus === 'error') {
        toast.custom(
          <Toast dismiss={() => setToastStatus(undefined)} duration={10000} position='inlineEnd' variant='error'>
            {onErrorMessage}
          </Toast>
        );
      }
    }
  }, [isEnabled, onErrorMessage, onSuccessMessage, toastStatus]);

  return { setToastStatus };
}
